import { useRef, type FC } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreatePosition } from 'modules/org_chart/hooks/useCreatePosition'
import { useJobPositionInformation } from 'modules/org_chart/hooks/useJobPositionInformation'

interface Props {
  managerId: string
  onClose: () => void
  refetch: () => void
}

const AddPositionModal: FC<Props> = ({ managerId, onClose, refetch }) => {
  const form = useRef(
    new Form(
      {},
      {
        jobPostingId: 'string',
        roleId: 'string',
        levelId: 'string',
      }
    )
  ).current
  const {
    data: { jobPostings, roles, levels },
    loading,
  } = useJobPositionInformation({
    roleIds: form.get('roleId').value ? [String(form.get('roleId').value)] : [],
  })
  const { loading: mutating, create: createPosition } = useCreatePosition()
  const { company: currentCompany } = useCurrentSession()

  const selectedRoleId = form.get('roleId').value
  const selectedJobPostingId = form.get('jobPostingId').value
  const selectedJobPosting = jobPostings.find((x) => x.id === selectedJobPostingId)
  const isSubmitDisabled = loading || !selectedJobPostingId || mutating

  const handleSubmit = async () => {
    const { jobPostingId, levelId } = form.values

    try {
      await createPosition({
        companyId: currentCompany.id,
        atsJobPostingId: jobPostingId,
        jobCatalogLevelId: levelId,
        managerId,
      })

      onClose()

      track(trackingEvents.orgChart.nodes.addPosition.form.submit, {
        is_successful: true,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })

      refetch()
    } catch (error) {
      track(trackingEvents.orgChart.nodes.addPosition.form.submit, {
        is_successful: false,
        error: String(error),
      })
    }
  }

  const jobPostingsOptions = jobPostings
    .filter((x) => x.status !== 'archived')
    .map(({ title, id, location }) => (
      <Selector.Option label={title} searchValue={title} key={id} value={id}>
        <Box flexDirection="column">
          <Text.Normal weight="medium">{title}</Text.Normal>
          {location && <Text.Normal>{location?.name}</Text.Normal>}
        </Box>
      </Selector.Option>
    ))

  const rolesOptions = roles
    .filter(
      (x) =>
        !x.archived &&
        selectedJobPosting &&
        x.legalEntitiesIds.includes(selectedJobPosting?.legalEntityId as string)
    )
    .map(({ name, id }) => (
      <Selector.Option label={name} searchValue={name} key={id} value={id}>
        {name}
      </Selector.Option>
    ))

  const levelsLength = levels.length
  const levelsOptions = levels
    .filter(({ isDefault }, _, arr) => arr.length === 1 || !isDefault)
    .map(({ id, name, isDefault }) => (
      <Selector.Option label={isDefault ? 'N/A' : name} searchValue={name} key={id} value={id}>
        {name}
      </Selector.Option>
    ))

  const handleJobPostingSelect = (value: unknown) => {
    track(trackingEvents.orgChart.nodes.addPosition.form.selectJobPosting)
    form.setValues({ jobPostingId: value })
  }

  const handleRoleSelect = (value: unknown) => {
    track(trackingEvents.orgChart.nodes.addPosition.form.selectJobRole)
    form.setValues({ roleId: value })
  }

  const handleLevelSelect = (value: unknown) => {
    track(trackingEvents.orgChart.nodes.addPosition.form.selectJobLevel)
    form.setValues({ levelId: value })
  }

  useUnsafeEffect(() => {
    if (levelsLength !== 1) return

    form.setValues({ levelId: levels[0]?.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levels])

  return (
    <Modal
      illustration="add-user"
      title={i18n.t('employees.orgchart.add_position_modal_title')}
      description={i18n.t('employees.orgchart.add_position_modal_description')}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Button disabled={isSubmitDisabled} submit>
          {i18n.t('common.submit')}
        </Button>
      }
    >
      <Box gap="s24">
        <Selector.Popover
          headerLabel={i18n.t('common.all')}
          placeholder={i18n.t('common.select')}
          label={i18n.t('employees.orgchart.link_to_job_opening')}
          blankLabel={i18n.t('common.empty')}
          onChange={handleJobPostingSelect}
          value={selectedJobPostingId ? [selectedJobPostingId] : []}
          searchPlaceholder={i18n.t('common.search')}
          isDisabled={loading}
          mandatory
        >
          {jobPostingsOptions || []}
        </Selector.Popover>
        <Selector.Popover
          headerLabel={i18n.t('common.all')}
          placeholder={i18n.t('common.select')}
          label={i18n.t('job_catalog.job_role')}
          blankLabel={i18n.t('common.empty')}
          onChange={handleRoleSelect}
          value={[form.get('roleId').value]}
          searchPlaceholder={i18n.t('common.search')}
          isDisabled={loading || rolesOptions.length === 0}
          mandatory
        >
          {rolesOptions || []}
        </Selector.Popover>
        <Selector.Popover
          isOpen={selectedRoleId}
          headerLabel={i18n.t('common.all')}
          placeholder={i18n.t('common.select')}
          label={i18n.t('job_catalog.level')}
          blankLabel={i18n.t('common.empty')}
          onChange={handleLevelSelect}
          value={[form.get('levelId').value]}
          searchPlaceholder={i18n.t('common.search')}
          isDisabled={!form.get('roleId').value || loading || levelsLength === 1}
          mandatory
        >
          {levelsOptions || []}
        </Selector.Popover>
      </Box>
    </Modal>
  )
}

export default observer(AddPositionModal)
